import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { CATEGORY_LABELS } from "@/features/finance/components/FinanceStatusBadge";
import { customersService } from "@/services/customers.service";
import { financeService } from "@/services/finance.service";

/**
 * Create a manual account (receivable or payable) with N equal installments.
 * Props:
 *  - open, onOpenChange
 *  - defaultType: 'receivable' | 'payable'
 *  - onSubmit: async (payload) => void
 *  - submitting
 */
export function CreateAccountDialog({
    open,
    onOpenChange,
    defaultType = "receivable",
    onSubmit,
    submitting,
}) {
    const [type, setType] = useState(defaultType);
    const [description, setDescription] = useState("");
    const [totalAmount, setTotalAmount] = useState("");
    const [installments, setInstallments] = useState("1");
    const [firstDueDate, setFirstDueDate] = useState(() =>
        new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10),
    );
    const [category, setCategory] = useState("outros");
    const [customerId, setCustomerId] = useState("");
    const [supplierId, setSupplierId] = useState("");
    const [freeName, setFreeName] = useState("");
    const [notes, setNotes] = useState("");
    const [error, setError] = useState("");

    // Reset all fields whenever the dialog is (re)opened for a given type.
    // The `setX` functions returned by useState are stable across renders per the
    // React docs, so we intentionally omit them from the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (open) {
            setType(defaultType);
            setDescription("");
            setTotalAmount("");
            setInstallments("1");
            setFirstDueDate(new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10));
            setCategory("outros");
            setCustomerId("");
            setSupplierId("");
            setFreeName("");
            setNotes("");
            setError("");
        }
    }, [open, defaultType]);

    const customersQ = useQuery({
        queryKey: ["customers-lite-finance"],
        queryFn: () => customersService.list({ pageSize: 200 }),
        enabled: open && type === "receivable",
        staleTime: 60_000,
    });
    const suppliersQ = useQuery({
        queryKey: ["suppliers-lite-finance"],
        queryFn: () => financeService.listSuppliers({ pageSize: 200 }),
        enabled: open && type === "payable",
        staleTime: 60_000,
    });

    const customers = customersQ.data?.items || [];
    const suppliers = suppliersQ.data?.items || [];

    async function handleSubmit(e) {
        e?.preventDefault();
        setError("");
        const total = Number((totalAmount || "0").replace(",", "."));
        if (!description.trim()) {
            setError("Descrição é obrigatória.");
            return;
        }
        if (!Number.isFinite(total) || total <= 0) {
            setError("Valor total inválido.");
            return;
        }
        const n = Math.max(1, Math.min(60, Number(installments) || 1));
        if (!firstDueDate) {
            setError("Informe a data do 1º vencimento.");
            return;
        }
        if (type === "receivable" && !customerId && !freeName.trim()) {
            setError("Selecione um cliente ou informe um nome.");
            return;
        }
        if (type === "payable" && !supplierId && !freeName.trim()) {
            setError("Selecione um fornecedor ou informe um nome.");
            return;
        }

        try {
            await onSubmit?.({
                type,
                description: description.trim(),
                total_amount: total,
                installments_count: n,
                first_due_date: firstDueDate,
                category,
                customer_id: type === "receivable" ? (customerId || undefined) : undefined,
                supplier_id: type === "payable" ? (supplierId || undefined) : undefined,
                counterparty_name: freeName.trim() || undefined,
                notes: notes.trim() || undefined,
            });
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || "Falha ao criar conta.");
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="create-account-dialog">
                <DialogHeader>
                    <DialogTitle>Criar conta</DialogTitle>
                    <DialogDescription>
                        Contas a Receber ou a Pagar com parcelamento simples (intervalo padrão de 30 dias).
                    </DialogDescription>
                </DialogHeader>

                <form className="space-y-4" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Tipo</Label>
                            <Select value={type} onValueChange={setType}>
                                <SelectTrigger data-testid="create-account-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="receivable">A Receber</SelectItem>
                                    <SelectItem value="payable">A Pagar</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Categoria</Label>
                            <Select value={category} onValueChange={setCategory}>
                                <SelectTrigger data-testid="create-account-category-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                                        <SelectItem key={v} value={v}>{l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="ca-desc">Descrição</Label>
                        <Input
                            id="ca-desc"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={type === "receivable" ? "Ex.: Contrato de manutenção mensal" : "Ex.: Aluguel do galpão"}
                            data-testid="create-account-description-input"
                        />
                    </div>

                    {type === "receivable" ? (
                        <div className="space-y-1.5">
                            <Label>Cliente</Label>
                            <Select value={customerId} onValueChange={setCustomerId}>
                                <SelectTrigger data-testid="create-account-customer-select">
                                    <SelectValue placeholder="Selecione um cliente cadastrado" />
                                </SelectTrigger>
                                <SelectContent>
                                    {customers.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.legal_name || c.trade_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                Ou informe um nome livre:
                            </p>
                            <Input
                                value={freeName}
                                onChange={(e) => setFreeName(e.target.value)}
                                placeholder="Ex.: Cliente esporádico"
                                data-testid="create-account-freename-input"
                            />
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            <Label>Fornecedor</Label>
                            <Select value={supplierId} onValueChange={setSupplierId}>
                                <SelectTrigger data-testid="create-account-supplier-select">
                                    <SelectValue placeholder="Selecione um fornecedor cadastrado" />
                                </SelectTrigger>
                                <SelectContent>
                                    {suppliers.length === 0 && (
                                        <SelectItem value="__empty" disabled>
                                            Nenhum fornecedor cadastrado
                                        </SelectItem>
                                    )}
                                    {suppliers.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>
                                            {s.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                Ou informe um nome livre:
                            </p>
                            <Input
                                value={freeName}
                                onChange={(e) => setFreeName(e.target.value)}
                                placeholder="Ex.: Fornecedor esporádico"
                                data-testid="create-account-freename-input"
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="ca-total">Valor total (R$)</Label>
                            <Input
                                id="ca-total"
                                type="number"
                                step="0.01"
                                min="0"
                                value={totalAmount}
                                onChange={(e) => setTotalAmount(e.target.value)}
                                placeholder="0,00"
                                data-testid="create-account-total-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="ca-inst">Parcelas</Label>
                            <Input
                                id="ca-inst"
                                type="number"
                                min="1"
                                max="60"
                                value={installments}
                                onChange={(e) => setInstallments(e.target.value)}
                                data-testid="create-account-installments-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="ca-first">1º vencimento</Label>
                            <Input
                                id="ca-first"
                                type="date"
                                value={firstDueDate}
                                onChange={(e) => setFirstDueDate(e.target.value)}
                                data-testid="create-account-first-due-input"
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="ca-notes">Observações (opcional)</Label>
                        <Textarea
                            id="ca-notes"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="create-account-notes-textarea"
                        />
                    </div>

                    {error && (
                        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                            {error}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange?.(false)}
                            data-testid="create-account-cancel-button"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            data-testid="create-account-submit-button"
                        >
                            {submitting ? "Criando..." : "Criar conta"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
